import { ButtonType, CheckBoxWithLabelGroupWidget, CheckBoxWithLabelProps, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, DialogWidget, HostedStackedElementContainer } from '@tableau/widgets';
import React from 'react';
import { Messages } from '../../codegen/Localize';
import { IBackgroundJobInfo } from '../types/BackgroundJobs';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { ICancelBackgroundJobParams } from '../types/CancelBackgroundJobRequest';
import { cancelBackgroundJobAction } from '../../app/actions/spaces/CancelBackgroundJobAction';
import { getAppStore } from '../../app/AppStore';
import { ServiceLocator } from '../../app/ServiceLocator';

export interface CancelBackgroundJobDialogProps {
  backgroundJob: IBackgroundJobInfo;
  close: () => void;
}

export interface CancelBackgroundJobDialogState extends ICancelBackgroundJobParams { }

export class CancelBackgroundJobDialog extends React.Component<CancelBackgroundJobDialogProps, CancelBackgroundJobDialogState> {

  private sessionManagementService: SessionManagementService;

  public static open(backgroundJob: IBackgroundJobInfo) {
    const hostContainer = new HostedStackedElementContainer();

    const close = () => {
      hostContainer && hostContainer.remove();
    };

    const dialog = React.createElement(CancelBackgroundJobDialog, { backgroundJob, close });
    hostContainer.render(dialog);
  }

  private static MAX_CANCELLATION_LENGTH = 500;

  constructor(props: CancelBackgroundJobDialogProps) {
    super(props);
    this.state = {
      backgroundJobId: props.backgroundJob.id + '',
      cancellationReason: '',
      notifyJobCreator: false,
      notifyProjectOwner: false,
      notifyServerAdministrators: false,
      notifySiteAdministrators: false,
    };
    this.cancelBackgroundJob = this.cancelBackgroundJob.bind(this);
    this.renderFooter = this.renderFooter.bind(this);
    this.sessionManagementService = getSessionManagementServiceInstance();
  }

  private cancelBackgroundJob() {
    getAppStore().dispatch(cancelBackgroundJobAction(this.state));
    this.props.close();
  }

  public renderFooter() {
    return <DialogFooterButtonsWidget
      buttons={[
        {
          handleClick: () => {
            this.props.close();
          },
          buttonType: ButtonType.Outline,
          children: Messages.cancel(),
          testId: 'footer-cancel'
        },
        {
          handleClick: this.cancelBackgroundJob,
          buttonType: ButtonType.Go,
          children: Messages.backgroundJobCancelDialogOk(),
          testId: 'footer-confirm'
        }
      ]}
      testId="cancel-dialog-footer"
    />;
  }

  public render() {
    const groupLabelId = 'background-job-cancel-dialog-checkbox-header';
    const checkboxProps: CheckBoxWithLabelProps[] = [
      {
        checked: this.state.notifyJobCreator,
        handleChange: (checked: boolean) => this.setState({ notifyJobCreator: checked }),
        label: Messages.backgroundJobCancelDialogJobCreator(),
        testId: 'checkbox-notify-project-leader',
      },
      {
        checked: this.state.notifyProjectOwner,
        handleChange: (checked: boolean) => this.setState({ notifyProjectOwner: checked }),
        label: Messages.backgroundJobCancelDialogProjectOwner(),
        testId: 'checkbox-notify-project-owner',
      },
      {
        checked: this.state.notifySiteAdministrators,
        handleChange: (checked: boolean) => this.setState({ notifySiteAdministrators: checked }),
        label: Messages.backgroundJobCancelDialogSiteAdministrators(),
        testId: 'checkbox-notify-site-administrators',
      },
      {
        checked: this.state.notifyServerAdministrators,
        disabled: !this.sessionManagementService.isSessionServerAdmin(),
        handleChange: (checked: boolean) => this.setState({ notifyServerAdministrators: checked }),
        label: Messages.backgroundJobCancelDialogServerAdministrators(),
        testId: 'checkbox-notify-server-administrators',
      },
    ];
    return <DialogWidget
      testId="cancel-dialog-inner"
      type={DialogType.NormalWithTitleBar}
      title={Messages.backgroundJobCancelDialogCancelJob()}
      onCloseRequest={this.props.close}
      showCloseButton={true}
      positioningBehavior={DialogPositioningBehavior.Centered}
      isModal={true}
      showFooter={true}
      handleGlassClick={this.props.close}
      isGlassTransparent={true}
      footerContent={this.renderFooter()}
      dialogWidth={500}
    >
      <span>{Messages.backgroundJobCancelDialogMessage()}</span>
      <textarea
        onChange={(event) => {
          const value = event.currentTarget.value;
          const truncatedValue = value.substr(0, CancelBackgroundJobDialog.MAX_CANCELLATION_LENGTH);
          this.setState({ cancellationReason: truncatedValue });
        }}
        value={this.state.cancellationReason}
        style={{
          width: '100%',
          height: '125px',
          marginTop: '18px',
          resize: 'none',
          boxSizing: 'border-box',
        }}
      />
      <span><p style={{
        marginTop: '0px',
        fontSize: '9px',
        color: this.state.cancellationReason.length === CancelBackgroundJobDialog.MAX_CANCELLATION_LENGTH
          ? 'red'
          : 'grey'
      }}>{this.state.cancellationReason.length + '/' + CancelBackgroundJobDialog.MAX_CANCELLATION_LENGTH}</p></span>
      <span id={groupLabelId} style={{paddingTop: '18px'}}>{Messages.backgroundJobCancelDialogCheckboxHeader()}</span>
      <div style={{paddingTop: '8px'}}>
        <CheckBoxWithLabelGroupWidget aria-labelledby={groupLabelId} checkBoxWithLabelPropsList={checkboxProps} />
      </div>
    </DialogWidget>;
  }
}
